<template>
	<div v-show="filteredCredits.length > 0">
		<div class="detail-infos__subheading">
			<h3 class="detail-infos__subheading--label">
				<slot />
			</h3>
		</div>

		<HiddenHorizontalScrollbar class="title-credits">
			<div
				class="title-credits__actor"
				v-for="(credit, index) of filteredCredits"
				:key="credit.personId + '-' + index"
			>
				<div class="title-credits__actor--img">
					<TitleCreditsPortrait :portraitUrl="credit.portraitUrl ?? undefined" :name="credit.name" />
				</div>
				<div>
					<TitleCreditName v-if="credit" :credit="credit" />
					<div class="title-credits__actor--role" v-if="credit.characterName">
						<div class="title-credits__actor--role--name">
							<strong :title="credit.characterName">{{ credit.characterName }}</strong>
						</div>
					</div>
				</div>
			</div>
			<div class="title-credits__actor title-credits__actor--empty">
				<BasicButton color="ghost" v-if="!hasLoadedMore" @click="loadMore"
					>{{ t('WEBAPP_LOAD_MORE') }}
				</BasicButton>
			</div>
		</HiddenHorizontalScrollbar>
	</div>
</template>

<script lang="ts" setup>
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import TitleCreditName from '@/components/details/TitleCreditName.vue';
import TitleCreditsPortrait from '@/components/details/TitleCreditsPortrait.vue';
import {
	GetAllCreditsDocument,
	GetAllCreditsQuery,
	GetAllCreditsQueryVariables,
} from '@/components/graphql/queries/GetAllCredits.query';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import BasicButton from '@/ui-components/BasicButton.vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		role?: string;
	}>(),
	{
		role: '',
	}
);

const { country, language } = storeToRefs(useLanguageStore());
const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();

const propsCredits = computed(() => {
	if (props.title.__typename === 'Season') {
		return props.title.show?.content.credits ?? [];
	}

	return props.title.content.credits ?? [];
});
// this means that there can't be loaded more credits because it doesn't even fill up the `first: 20` limit
const hasLoadedMore = ref(propsCredits.value.length < 20);

const { result } = useQuery<GetAllCreditsQuery, GetAllCreditsQueryVariables>(
	GetAllCreditsDocument,
	() => ({
		fullPath: activeRoute.value.path,
		country: country.value,
		language: language.value,
	}),
	() => ({
		enabled: hasLoadedMore.value,
		errorPolicy: 'all',
		prefetch: false,
	})
);
const allCredits = computed(() => {
	const node = result.value?.urlV2.node;
	if (node?.__typename === 'Season') {
		return node.show?.content.credits ?? [];
	}
	if (node?.__typename === 'Movie' || node?.__typename === 'Show') {
		return node.content?.credits ?? [];
	}
	return [];
});

const credits = computed(() => {
	if (allCredits.value?.length > 0) {
		return allCredits.value;
	}
	return propsCredits.value;
});

const filteredCredits = computed(() => {
	if (!props.role) {
		return credits.value ?? [];
	}

	return (credits.value ?? []).filter(credit => credit.role === props.role);
});

async function loadMore() {
	hasLoadedMore.value = true;
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

$credits-width: 130px;

.title-credits {
	margin-bottom: 15px;
	padding-bottom: 5px;
	color: $jw-color-lightgray--dark;

	.hidden-horizontal-scrollbar__items {
		align-items: initial;
	}

	&__actor {
		margin-inline-start: 15px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		background-color: #101720;
		padding: 16px;
		border-radius: 8px;
		max-width: 200px;
		width: 200px;
		line-height: 200%;
		text-align: center;

		&--empty {
			background-color: transparent;
		}

		&:first-child {
			margin-inline-start: 0;
		}

		.platform-ios,
		.platform-android & {
			margin-inline-start: 15px !important;
		}

		&--img {
			height: 88px;
			width: 88px;
		}

		&--name {
			display: block;
			width: $credits-width;
			text-overflow: ellipsis;
			overflow: hidden;
		}

		&--role {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			max-width: $credits-width;

			&--name {
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}
	}
}
</style>
